import React from "react";
import { LayersControl, MapContainer, Marker, Popup, ScaleControl, TileLayer, Tooltip, ZoomControl } from "react-leaflet";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LatLngBoundsExpression, LeafletEvent, Map } from "leaflet";
import { shallowEqual } from "react-redux";

import { busIconBlack, busIconBlue } from "../utils/mapIcons";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
  dispatchCoordinates,
  dispatchCurrentZoom,
  dispatchIsMapReady,
  resetDataActiveBus,
  setActiveBus,
} from "../redux/actions";
import AccordionMapLineLayer from "./views/AccordionMapLineLayer";
import { traccarCredentials, maxBounds, configAgencyName } from "../config";
import MapHooks from "./MapHooks";
import SingleReportAVLMap from "./reports/SingleReportAVLMap";
import TableBigComponentMap from "./TableBigComponentMap";
import DeviationReportTableMap from "./reports/DeviationReportTableMap";
import LeafletRuler from "./services/useLeafletRuler";
import SingleTripReportMap from "./reports/SingleTripReportMap";
import Last24HoursReportMap from "./reports/Last24HoursReportMap";
import MapBusesComponent, { OccupancyStatusDiv } from "./MapBusesComponent";
import MapAllRoutes from "./MapAllRoutes";
import MapTechnicalVehicles from "./MapTechnicalVehicles";
import subcontractorIcon from "../assets/icons/subcontractorIcon.svg";
import variableColors from "../_App.module.scss";
import SelectedBusViewMap from "./views/SelectedBusViewMap";
import RoutesInfoMap from "./views/RoutesInfoMap";
import LeafletLoading from "./services/useLeafletLoading";
import BusLocationByDateMap from "./reports/BusLocationByDateMap";
import OccupancyStatusIcon from "./helpers/OccupancyStatusIcon";
import AllStopsPunctualityReportMap from "./reports/AllStopsPunctualityReportMap";
import AverageSpeedsMap from "./swiftly/AverageSpeedsMap";
import HeadwayPerformanceMap from "./swiftly/HeadwayPerformanceMap";
import { getHeadwayStatusColor } from "../utils/swiftlyHelpers";
import { dynamicTextColor } from "../utils/helpers";
import MapSelectBusModal from "./MapSelectBusModal";

const { secondaryColor } = variableColors;

const DivMap = styled.div`
  width: 100%;
  height: calc(100vh - var(--constantHeights));
`;

//* The main component
const MapComponent = ({
  showLineNumbers,
  buses,
  showDeviations,
  colorMap,
  showAllRoutes,
  showTechnicalVehicles,
  initialMapRange,
  selectedMapUrl,
  setSelectedMapUrl,
  agencyId,
}: {
  showLineNumbers: boolean;
  buses: {
    busesOnTime: BusInfo[];
    busesTooFast: BusInfo[];
    delayedBuses: BusInfo[];
    remainingBuses: BusInfo[];
    busesWithLayover: BusInfo[];
  };
  showDeviations: boolean;
  colorMap: boolean;
  showAllRoutes: boolean;
  showTechnicalVehicles: boolean;
  initialMapRange: LatLngBoundsExpression;
  selectedMapUrl: string;
  agencyId: string;
  setSelectedMapUrl: (str: string) => void;
}): JSX.Element => {
  //* Mapbox api key
  const mapboxApiKey =
    process.env.NODE_ENV === "production" && configAgencyName === ("Swarzedz" as string)
      ? (process.env.REACT_APP_MAPBOX_API_KEY_Sluz as string)
      : (process.env.REACT_APP_MAPBOX_API_KEY_priv as string);

  const dispatch: Dispatch = useAppDispatch();

  const [onlyOneLine, showRoutesTable, kindOfTimeDeviation, filterByRouteId]: [boolean, boolean, string, string] =
    useAppSelector(
      (state: RootState) => [
        state?.lineShapeStops?.onlyOneLine,
        state?.constantValues?.showRoutes_Table,
        state?.mapView?.kindOfTimeDeviation,
        state?.mapView?.filterByRouteId,
      ],
      shallowEqual
    );
  // console.log("filterByRouteId:", filterByRouteId);

  const location = useLocation();

  const [showReportAVL, setShowReportAVL] = React.useState<boolean>(false);
  const [showTableRoute, setShowTableRoute] = React.useState<boolean>(false);
  const [busesToShow, setBusesToShow] = React.useState<{
    busesOnTime: BusInfo[];
    busesTooFast: BusInfo[];
    delayedBuses: BusInfo[];
    remainingBuses: BusInfo[];
    busesWithLayover: BusInfo[];
  } | null>(null);
  const [mapView, setMapView] = React.useState<Map | null>(null);

  //* Dispatching zoom to the redux state onLoad + isMapReady status
  React.useEffect(() => {
    dispatch(dispatchIsMapReady(!!mapView));
    if (mapView) {
      const currentZoom = mapView.getZoom();
      // console.log({ currentZoom });
      dispatch(dispatchCurrentZoom(currentZoom));
    }
  }, [dispatch, mapView]);

  //* Dispatching zoom to the redux state on zoom_on/zoom_of
  React.useEffect(() => {
    if (!mapView) return;
    mapView.on("zoomend", function () {
      const currentZoom = mapView.getZoom();
      // console.log({ currentZoom });
      dispatch(dispatchCurrentZoom(currentZoom));
    });
  }, [dispatch, mapView]);

  React.useEffect(() => {
    if (buses) {
      const pathName: string = location?.pathname;
      if (pathName === "/") {
        const emptyBuses = { busesOnTime: [], busesTooFast: [], delayedBuses: [], busesWithLayover: [] };

        let busesToSet = (
          kindOfTimeDeviation === "workingBuses"
            ? {
                delayedBuses: buses?.delayedBuses,
                busesOnTime: buses?.busesOnTime,
                busesTooFast: buses?.busesTooFast,
                busesWithLayover: buses?.busesWithLayover,
              }
            : kindOfTimeDeviation === "delayedBuses"
            ? { ...emptyBuses, delayedBuses: buses?.delayedBuses }
            : kindOfTimeDeviation === "busesOnTime"
            ? { ...emptyBuses, busesOnTime: buses?.busesOnTime }
            : kindOfTimeDeviation === "busesTooFast"
            ? { ...emptyBuses, busesTooFast: buses?.busesTooFast }
            : kindOfTimeDeviation === "busesWithLayover"
            ? { ...emptyBuses, busesWithLayover: buses?.busesWithLayover }
            : emptyBuses
        ) as {
          busesOnTime: BusInfo[];
          busesTooFast: BusInfo[];
          delayedBuses: BusInfo[];
          remainingBuses: BusInfo[];
          busesWithLayover: BusInfo[];
        };
        // console.log("busesToSet:", busesToSet);

        //* Filter buses by routeId
        if (filterByRouteId && filterByRouteId !== "allRoutes") {
          // console.log("filterByRouteId:", filterByRouteId);

          busesToSet = {
            delayedBuses: busesToSet?.delayedBuses?.filter((bus: BusInfo) => bus?.routeId === filterByRouteId),
            busesOnTime: busesToSet?.busesOnTime?.filter((bus: BusInfo) => bus?.routeId === filterByRouteId),
            busesTooFast: busesToSet?.busesTooFast?.filter((bus: BusInfo) => bus?.routeId === filterByRouteId),
            busesWithLayover: busesToSet?.busesWithLayover?.filter((bus: BusInfo) => bus?.routeId === filterByRouteId),
          } as {
            busesOnTime: BusInfo[];
            busesTooFast: BusInfo[];
            delayedBuses: BusInfo[];
            remainingBuses: BusInfo[];
            busesWithLayover: BusInfo[];
          };
        }

        setBusesToShow(busesToSet);
      } else {
        setBusesToShow(buses);
      }
    }
  }, [buses, filterByRouteId, kindOfTimeDeviation, location]);

  React.useEffect(() => {
    const pathName = location?.pathname;
    // console.log({ pathName });
    const pathNameReportAVL_Boolean = pathName.includes("/singleAVL");
    setShowReportAVL(pathNameReportAVL_Boolean);
    // console.log({ pathNameReportAVL_Boolean });
  }, [location]);

  React.useEffect(() => {
    const pathName = location?.pathname;
    // console.log({ pathName });
    const pathNameTableRoute_Boolean = pathName === "/" ? true : false;
    setShowTableRoute(pathNameTableRoute_Boolean);
  }, [location?.pathname]);

  //* Tile layers config
  const tileLayerProps = {
    updateWhenIdle: true,
    updateWhenZooming: false,
    keepBuffer: 3,
    errorTileUrl: `${window.location.origin + "/errorIcon.svg"}`,
    noWrap: true,
  };

  return (
    <React.Fragment>
      <DivMap>
        <MapContainer
          id="mapContainer"
          ref={setMapView}
          zoomSnap={0.1}
          zoomDelta={0.2}
          maxZoom={18}
          minZoom={6.5}
          bounds={initialMapRange}
          maxBounds={maxBounds as LatLngBoundsExpression}
          scrollWheelZoom={true}
          style={{ width: "100%", height: "100%", position: "relative" }}
          //* Disable below, but <ZoomControl position="topright" /> enable
          zoomControl={false}
          doubleClickZoom={false}
          className={!colorMap ? "leaflet-grayscale" : ""}
          dragging={true}
          zoomAnimation={true}
          zoomAnimationThreshold={4}
        >
          {/* //*Traffic maps only in dev mod + for Swarzędz */}
          {process.env.NODE_ENV === "development" || configAgencyName === ("Swarzedz" as string) ? (
            <LayersControl position="topright">
              <LayersControl.BaseLayer
                name="Open Street Map"
                checked={selectedMapUrl?.includes("openstreetmap") ? true : false}
              >
                <TileLayer
                  {...tileLayerProps}
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  eventHandlers={{
                    add: (event: LeafletEvent) => setSelectedMapUrl(event?.target?._url?.split("/{z}/{x}/{y}")?.[0]),
                    // loading: (event: LeafletEvent) => console.log("Loading", event),
                    // load: (event: LeafletEvent) => console.log("Load", event),
                  }}
                />
              </LayersControl.BaseLayer>

              <LayersControl.BaseLayer name="Mapbox Traffic Map" checked={selectedMapUrl?.includes("mapbox") ? true : false}>
                <TileLayer
                  {...tileLayerProps}
                  tileSize={512}
                  zoomOffset={-1}
                  attribution='&copy; <a href="https://www.mapbox.com/">Mapbox</a> | Traffic data'
                  url={`https://api.mapbox.com/styles/v1/mapbox/traffic-day-v1/tiles/{z}/{x}/{y}?access_token=${mapboxApiKey}`}
                  // url={`https://api.mapbox.com/v4/mapbox.terrain-rgb/{zoom}/{x}/{y}.pngraw?access_token=${mapboxApiKey}`} //* Elevation data
                  eventHandlers={{
                    add: (event) => setSelectedMapUrl(event?.target?._url?.split("/{z}/{x}/{y}")?.[0]),
                  }}
                />
              </LayersControl.BaseLayer>
            </LayersControl>
          ) : (
            <TileLayer
              {...tileLayerProps}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              eventHandlers={{
                add: (event) => setSelectedMapUrl(event?.target?._url?.split("/{z}/{x}/{y}")?.[0]),
              }}
            />
          )}

          <ZoomControl position="topleft" />
          <ScaleControl position="bottomleft" />

          {/* //Todo: React.useState change to location.pathname */}
          {!onlyOneLine &&
          !showReportAVL &&
          !location?.pathname.includes("/bus") &&
          !location?.pathname.includes("/routesInfo") &&
          !location?.pathname.includes("/singleTrip") &&
          !location?.pathname.includes("/last24hours") &&
          !location?.pathname.includes("/locationByDate") &&
          !location?.pathname.includes("/allStopsPunctualityReportDownLoad") &&
          !location?.pathname.includes("/averageSpeeds") &&
          !location?.pathname.includes("/headwayPerformance") ? (
            <React.Fragment>
              {/* //* Unassigned buses icons */}
              <RemainingBusesIcons remainingBuses={buses?.remainingBuses} showLineNumbers={showLineNumbers} />

              {busesToShow && busesToShow.busesOnTime ? (
                <MapBusesComponent
                  showLineNumbers={showLineNumbers}
                  busesOnMap={busesToShow?.busesOnTime}
                  showDeviations={showDeviations}
                  moreActions={true}
                />
              ) : null}
              {busesToShow && busesToShow.busesTooFast ? (
                <MapBusesComponent
                  showLineNumbers={showLineNumbers}
                  busesOnMap={busesToShow?.busesTooFast}
                  showDeviations={showDeviations}
                  moreActions={true}
                />
              ) : null}
              {busesToShow && busesToShow.delayedBuses ? (
                <MapBusesComponent
                  showLineNumbers={showLineNumbers}
                  busesOnMap={busesToShow?.delayedBuses}
                  showDeviations={showDeviations}
                  moreActions={true}
                />
              ) : null}
              {busesToShow && busesToShow.busesWithLayover ? (
                <MapBusesComponent
                  showLineNumbers={showLineNumbers}
                  busesOnMap={busesToShow?.busesWithLayover}
                  showDeviations={showDeviations}
                  moreActions={true}
                />
              ) : null}
            </React.Fragment>
          ) : null}

          {/* //* Select bus modal */}
          {busesToShow && (location.pathname === "/" || location?.pathname.includes("/views/accordion")) ? (
            <MapSelectBusModal busesToShow={busesToShow!} remainingBuses={buses?.remainingBuses} />
          ) : null}

          {location?.pathname.includes("/modifyTripStatus") || (showTableRoute && showRoutesTable) ? (
            <TableBigComponentMap />
          ) : null}
          {location?.pathname.includes("/views/accordion") && (
            <AccordionMapLineLayer showLineNumbers={showLineNumbers} buses={buses!} showDeviations={showDeviations} />
          )}

          {location?.pathname.includes("/reports/singleAVL") ? <SingleReportAVLMap /> : null}
          {location?.pathname.includes("/deviation") ? <DeviationReportTableMap /> : null}
          {location?.pathname.includes("/singleTrip") ? <SingleTripReportMap showLineNumbers={showLineNumbers} /> : null}
          {location?.pathname.includes("/last24hours") ? <Last24HoursReportMap /> : null}
          {location?.pathname.includes("/locationByDate") ? <BusLocationByDateMap /> : null}
          {location?.pathname.includes("/averageSpeeds") ? <AverageSpeedsMap /> : null}
          {location?.pathname.includes("/headwayPerformance") ? (
            <HeadwayPerformanceMap buses={buses} showLineNumbers={showLineNumbers} />
          ) : null}

          {/* //* Show all routes - only the main page! */}
          {showAllRoutes && location.pathname === "/" ? <MapAllRoutes agencyId={agencyId} /> : null}

          {/* //* Show technical vehicles -> PKS only! */}
          {traccarCredentials &&
          Object.keys(traccarCredentials).length &&
          showTechnicalVehicles &&
          (location.pathname === "/" || location?.pathname.includes("/views/accordion")) ? (
            <MapTechnicalVehicles showLineNumbers={showLineNumbers} />
          ) : null}

          {/* //* SelectedBusView component */}
          {location?.pathname.includes("/views/bus") ? (
            <SelectedBusViewMap showLineNumbers={showLineNumbers} showDeviations={showDeviations} />
          ) : null}

          {/* //* RoutesInfoMap component */}
          {location?.pathname.includes("/routesInfo") ? <RoutesInfoMap agencyId={agencyId} /> : null}

          {/* //* AllStopsPunctualityReportMap component */}
          {location?.pathname.includes("/allStopsPunctualityReportDownLoad") ? <AllStopsPunctualityReportMap /> : null}

          {/* //* Custom Hooks */}
          <MapHooks />
          <LeafletRuler />
          <LeafletLoading />
          {/* //* Test only <MapEventsHandler />  */}
        </MapContainer>
      </DivMap>
    </React.Fragment>
  );
};

export default MapComponent;

//* Unassigned buses icons
export const RemainingBusesIcons = ({
  remainingBuses,
  showLineNumbers,
}: {
  remainingBuses: BusInfo[];
  showLineNumbers: boolean;
}): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();

  const dispatch: Dispatch = useAppDispatch();

  const [activeBus, currentZoomFromRedux]: [{ busId: string }, number] = useAppSelector(
    (state: RootState) => [state?.buses?.activeBus, state?.constantValues?.currentZoom],
    shallowEqual
  );

  const remainingBusesIcons: JSX.Element[] = remainingBuses?.map(
    (elem: BusInfo, index: number): JSX.Element => (
      <Marker
        key={elem?.id}
        position={{
          lat: elem?.loc?.lat ?? 0,
          lng: elem?.loc?.lon ?? 0,
        }}
        icon={activeBus && activeBus.busId === elem.id ? busIconBlue : busIconBlack}
        eventHandlers={
          location.pathname !== "/" && !location?.pathname.includes("/views/accordion")
            ? {
                click: async () => {
                  await dispatch(setActiveBus(elem.id));
                  await dispatch(resetDataActiveBus());
                  currentZoomFromRedux &&
                    (await dispatch(dispatchCoordinates(elem.loc.lat, elem.loc.lon, currentZoomFromRedux)));
                },
              }
            : undefined
        }
      >
        {showLineNumbers && (
          <Tooltip permanent={true} direction={index % 2 === 0 ? "right" : "left"}>
            {elem?.vehicleName || elem?.id || `${t("b/d")}`}
            {elem.subcontractorPKS ? (
              <span style={{ marginLeft: "0.25rem", color: secondaryColor }} className="span_italic span_sub">
                <img
                  width={16}
                  height={16}
                  src={subcontractorIcon}
                  alt="Subcontractor Icon"
                  style={{ marginRight: "0.25rem" }}
                />
                {t("PW")}
              </span>
            ) : null}
          </Tooltip>
        )}
        {activeBus && (activeBus.busId === "" || activeBus.busId === elem.id) && (
          <Popup>
            {t("Stan zadania")}: <span className="span_bold">{elem.blockMthd || t("b/d")}</span>
            <br />
            {t("Pojazd")} - {t("Nazwa")} <span className="span_bold">{elem?.vehicleName || `${t("b/d")}`}</span>
            <br />
            {t("Pojazd")} - {t("Id")} <span className="span_bold">{elem?.id || `${t("b/d")}`}</span>
            <br />
            {t("Pojazd")} - {t("Dodatkowe info")} <span className="span_bold">{elem?.pksName || `${t("b/d")}`}</span>
            <br />
            {t("VehicleType")} <span className="span_bold">{t(elem?.vehicleTypeName) || `${t("b/d")}`}</span>
            {configAgencyName === ("PoznanPKS" as string) ? (
              <React.Fragment>
                <br />
                {t("Podwykonawca")}: <span className="span_bold">{elem.subcontractorPKS?.toString()}</span>
                {elem.subcontractorPKS ? (
                  <span style={{ marginLeft: "0.25rem", color: secondaryColor }} className="span_italic">
                    <img
                      width={16}
                      height={16}
                      src={subcontractorIcon}
                      alt="Subcontractor Icon"
                      style={{ marginRight: "0.25rem" }}
                    />
                  </span>
                ) : null}
              </React.Fragment>
            ) : null}
            <br />
            <OccupancyStatusDiv>
              {t("Status zajętości")}:{" "}
              <OccupancyStatusIcon busInfo={elem} isHistorical={false} addText={true} isBold={true} />
            </OccupancyStatusDiv>
            {t("Headway Status")}:{" "}
            <span
              className="span_bold"
              style={{
                background: getHeadwayStatusColor(elem?.headwayDeviationCategory),
                color: dynamicTextColor(getHeadwayStatusColor(elem?.headwayDeviationCategory)),
                fontWeight: "bold",
                padding: "1px 4px",
              }}
            >
              {t(elem?.headwayDeviationCategory) || `${t("b/d")}`}
            </span>
          </Popup>
        )}
      </Marker>
    )
  );

  return <React.Fragment>{remainingBusesIcons}</React.Fragment>;
};
